/**
 * Settings — who holds which role, what each role may do, and what the system
 * is currently allowed to do at all.
 *
 * Until P13 this was a `PagePlaceholder`, which meant three things had no home:
 *
 *  1. **Onboarding.** A new colleague signs in and has no role. Somebody has to
 *     give them one, and the only way to do that was a hand-written INSERT
 *     against production. Now it is a text field and a dropdown — and every use
 *     of it lands in two ledgers.
 *
 *  2. **The permission matrix.** Seeded by migration 07 and, since applied
 *     migrations are frozen, unchangeable without a new one. Roman's call
 *     (2026-08-26) was to make it editable here; the endpoint carries the guard
 *     set that makes that safe.
 *
 *  3. **"Can this thing mail a customer right now?"** `GET /api/v1/agent/status`
 *     has existed since P7 and no screen ever called it. The two send locks
 *     (`DRA_SEND_ENABLED` and the recipient allowlist) were readable only in the
 *     Azure portal. They are the first thing on this page now.
 *
 * ONE THING THE UI HAS TO SAY OUT LOUD: a role change reaches OTHER people on
 * their next page load. The server cache is invalidated immediately, but their
 * browser fetched its profile once when they opened the app. Pretending the
 * change is instant for everybody would be the kind of small lie that costs an
 * afternoon of debugging.
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Loader2,
  Lock,
  Mail,
  Plus,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  useAgentStatus,
  usePermissionMatrix,
  useSettingsRoles,
  useSettingsUsers,
  useUpdatePermissionMatrix,
  useUpdateUserRole,
} from '../hooks/useSettingsApi';
import { useToast } from '../components/ui/Toast';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { ApiRequestError } from '../services/api';
import type { MatrixGrant, PermissionMatrix, SettingsRole, SettingsUser } from '../types/domain';

import { Button } from '../components/ui/Button';
import { FOCUS_RING_TIGHT } from '../components/ui/focus';
import { Field, Input, Select } from '../components/ui/Field';
import { isoDate, isoInstant } from '../lib/dates';
function errorMessage(err: unknown): string {
    if (err instanceof ApiRequestError) return err.message;
    if (err instanceof Error) return err.message;
    return 'Something went wrong';
}

function Section({ title, icon: Icon, subtitle, children }: {
    title: string;
    icon: typeof Users;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <section className="rounded-lg border border-gray-200 bg-white">
            <header className="border-b border-gray-200 px-4 py-3">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <Icon className="h-4 w-4 text-muted" />
                    {title}
                </h2>
                {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
            </header>
            <div className="p-4">{children}</div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// System status — the two send locks, then the queues
// ---------------------------------------------------------------------------

function Lamp({ on, onLabel, offLabel, danger }: { on: boolean; onLabel: string; offLabel: string; danger?: boolean }) {
    const tone = on
        ? danger ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-green-50 text-green-800 border-green-200'
        : 'bg-gray-50 text-gray-600 border-gray-200';
    return <span className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${tone}`}>{on ? onLabel : offLabel}</span>;
}

function SystemStatus() {
    const { data: status, isLoading, error } = useAgentStatus();

    if (isLoading) return <div className="text-sm text-gray-500">Loading…</div>;
    if (error || !status) return <div className="text-sm text-gray-500">Status unavailable — {errorMessage(error)}</div>;

    const domains = status.recipientPolicy.allowedDomains;

    return (
        <div className="space-y-4">
            <dl className="grid gap-4 sm:grid-cols-3">
                <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Mail agent</dt>
                    <dd className="mt-1">
                        <Lamp on={status.agentMode} onLabel="Timers running" offLabel="Timers off (DRA_AGENT_MODE)" />
                    </dd>
                    <p className="mt-1 text-xs text-gray-500">
                        Only the worker Function App registers timers. The dashboard never does — that is a platform fact,
                        not a fault.
                    </p>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Outbound send</dt>
                    <dd className="mt-1">
                        <Lamp on={status.sendEnabled} onLabel="ARMED — approving sends mail" offLabel="Disarmed — approving sends nothing" danger />
                    </dd>
                    <p className="mt-1 text-xs text-gray-500">
                        `DRA_SEND_ENABLED`. Every outbound mail still goes through the approval queue either way.
                    </p>
                </div>
                <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Recipients allowed</dt>
                    <dd className="mt-1">
                        {status.recipientPolicy.unrestricted ? (
                            <span className="inline-block rounded border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">
                                Unrestricted ({status.recipientPolicy.allowAllToken})
                            </span>
                        ) : (
                            <span className="flex flex-wrap gap-1">
                                {(domains ?? []).map(d => (
                                    <span key={d} className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-700">
                                        {d}
                                    </span>
                                ))}
                            </span>
                        )}
                    </dd>
                    <p className="mt-1 text-xs text-gray-500">
                        {status.recipientPolicy.configured
                            ? 'Configured via DRA_ALLOWED_RECIPIENT_DOMAINS.'
                            : 'Nothing configured — these are the built-in pilot defaults, not "no restriction".'}
                    </p>
                </div>
            </dl>

            <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 text-sm sm:grid-cols-4">
                {[
                    ['Pending mails', status.pendingCommunications],
                    ['Failed / poison', status.failedCommunications + status.poisonCommunications],
                    ['Tickets in review', status.ticketsInReview],
                    ['Overdue authorizations', status.overdueAuthorizationRequests],
                ].map(([label, value]) => (
                    <div key={String(label)}>
                        <div className="text-lg font-semibold text-gray-900">{value}</div>
                        <div className="text-xs text-gray-500">{label}</div>
                    </div>
                ))}
            </div>
            <p className="text-xs text-muted">
                {/*
                  * P38, task 7 (D5). This printed the column raw —
                  * `2026-09-28T07:47:59Z` — which is accurate and is the one figure on
                  * the page a reader has to COMPARE with something (a mail's own
                  * timestamp, an audit row). `isoInstant` keeps the UTC, because that
                  * is part of the fact here: the timer runs in UTC and a local clock
                  * would quietly shift it by an hour or two.
                  *
                  * The words beside it are A6's finding and are not cosmetic: a
                  * checkpoint moving is not proof the poll is alive.
                  */}
                Last mailbox poll checkpoint: {status.lastPollCheckpoint ? isoInstant(status.lastPollCheckpoint) : 'never'}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function RoleChip({ name, active, onRemove, disabled }: {
    name: string;
    active: boolean;
    onRemove?: () => void;
    disabled?: boolean;
}) {
    return (
        <span
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${
                active ? 'border-blue-200 bg-blue-50 text-blue-800' : 'border-gray-200 bg-gray-50 text-muted line-through'
            }`}
            title={active ? undefined : 'Previously held — the row stays so it can be reactivated'}
        >
            {name}
            {/* P40: this one chip renders once per role per person, so the eleven
                ring-less controls the probe found on /settings were all this button. It
                also gets an `aria-label`: it was named by `title` alone, which does
                compute an accessible name but is the weaker form (the audit's S1-04
                class, and the one note `scan-interactive-semantics` was printing).
                The comment sits OUTSIDE the `&& (` - inside it is a syntax error, and
                this is the third time in two phases. */}
            {active && onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    aria-label={`Remove the ${name} role`}
                    className={`rounded-ctl hover:bg-info-100 disabled:opacity-40 ${FOCUS_RING_TIGHT}`}
                    title={`Remove ${name}`}
                >
                    <X className="h-3 w-3" />
                </button>
            )}
        </span>
    );
}

export function UserRow({ user, roles, canEdit, busy, onChange }: {
    user: SettingsUser;
    roles: SettingsRole[];
    canEdit: boolean;
    busy: boolean;
    onChange: (email: string, roleId: number, action: 'assign' | 'remove', roleName: string) => void;
}) {
    const [adding, setAdding] = useState('');
    const heldActive = new Set(user.roles.filter(r => r.isActive).map(r => r.roleId));
    const assignable = roles.filter(r => r.isActive && !heldActive.has(r.id));

    return (
        <tr className="border-t border-gray-100">
            <td className="px-3 py-2 align-top">
                <div className="text-sm text-gray-900">{user.email}</div>
                {user.isAdmin && <div className="text-xs text-amber-700">Administrator</div>}
            </td>
            <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap items-center gap-1">
                    {user.roles.length === 0 && <span className="text-xs text-muted">no roles</span>}
                    {user.roles.map(r => (
                        <RoleChip
                            key={r.roleId}
                            name={r.roleName}
                            active={r.isActive}
                            disabled={!canEdit || busy}
                            onRemove={() => onChange(user.email, r.roleId, 'remove', r.roleName)}
                        />
                    ))}
                </div>
            </td>
            <td className="px-3 py-2 align-top text-right">
                {canEdit && assignable.length > 0 && (
                    <div className="flex items-center justify-end gap-1">
                        {/* P40: the kit's Select - it carries the focus ring and the
                            house height. `aria-label` because the only label this control
                            could have is its own first option. */}
                        <Select
                            aria-label="Add a role to this person"
                            value={adding}
                            onChange={e => setAdding(e.target.value)}
                            className="w-auto text-xs"
                        >
                            <option value="">Add role…</option>
                            {assignable.map(r => (
                                <option key={r.id} value={r.id}>{r.roleName}</option>
                            ))}
                        </Select>
                        <Button variant="primary" size="sm"
                            aria-label="Add the selected role to this user"
                            disabled={!adding || busy}
                            onClick={() => {
                                const role = roles.find(r => r.id === Number(adding));
                                if (!role) return;
                                onChange(user.email, role.id, 'assign', role.roleName);
                                setAdding('');
                            }}
                        >
                            <Plus className="h-3 w-3" />
                        </Button>
                    </div>
                )}
            </td>
        </tr>
    );
}

function AddUserForm({ roles, busy, onAdd }: {
    roles: SettingsRole[];
    busy: boolean;
    onAdd: (email: string, roleId: number, roleName: string) => void;
}) {
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');

    const submit = () => {
        const role = roles.find(r => r.id === Number(roleId));
        if (!email.trim() || !role) return;
        onAdd(email.trim(), role.id, role.roleName);
        setEmail('');
        setRoleId('');
    };

    return (
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-gray-100 pt-4">
            {/* P40: the form that grants somebody access to this system had two labels
                wired to nothing and two controls with no focus ring (audit S2-20, S2-11).
                `Field` generates the id, so both are fixed by construction. */}
            <Field label="Dolder e-mail address" className="w-72">
                {(a) => (
                    <Input
                        {...a}
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && submit()}
                        placeholder="firstname.lastname@dolder.com"
                    />
                )}
            </Field>
            <Field label="Role">
                {(a) => (
                <Select
                    {...a}
                    value={roleId}
                    onChange={e => setRoleId(e.target.value)}
                    className="w-auto"
                >
                    <option value="">Choose…</option>
                    {roles.filter(r => r.isActive).map(r => (
                        <option key={r.id} value={r.id}>{r.roleName}</option>
                    ))}
                </Select>
                )}
            </Field>
            <Button variant="primary" size="sm"
                onClick={submit}
                disabled={busy || !email.trim() || !roleId}
            >
                Assign
            </Button>
            <p className="w-full text-xs text-gray-500">
                The address is not verified against Entra ID — DRA has no user table, it only records which address holds
                which role. A typo simply creates an assignment nobody can use.
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Permission matrix
// ---------------------------------------------------------------------------

function grantOf(matrix: PermissionMatrix, roleId: number, pageId: number): MatrixGrant | undefined {
    return matrix.grants.find(g => g.roleId === roleId && g.pageId === pageId);
}

function MatrixTable({ matrix, canEdit, busy, onSet }: {
    matrix: PermissionMatrix;
    canEdit: boolean;
    busy: boolean;
    onSet: (roleId: number, pageId: number, canView: boolean, canEdit: boolean, label: string) => void;
}) {
    const editableRoles = matrix.roles.filter(r => !r.bypassesMatrix);

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2 font-medium">Page</th>
                        {editableRoles.map(r => (
                            <th key={r.id} className="px-3 py-2 text-center font-medium">{r.roleName}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {matrix.pages.map(page => (
                        <tr key={page.id} className="border-t border-gray-100">
                            <td className="px-3 py-2">
                                <div className="text-gray-900">{page.pageName}</div>
                                <div className="text-xs text-muted">{page.pageUrl}</div>
                            </td>
                            {editableRoles.map(role => {
                                const grant = grantOf(matrix, role.id, page.id);
                                const view = grant?.canView ?? false;
                                const edit = grant?.canEdit ?? false;
                                if (page.adminOnly) {
                                    return (
                                        <td key={role.id} className="px-3 py-2 text-center">
                                            <span className="inline-flex items-center gap-1 text-xs text-muted">
                                                <Lock className="h-3 w-3" /> Admin only
                                            </span>
                                        </td>
                                    );
                                }
                                return (
                                    <td key={role.id} className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-3">
                                            <label className="flex items-center gap-1 text-xs text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={view}
                                                    disabled={!canEdit || busy}
                                                    onChange={e => {
                                                        const nextView = e.target.checked;
                                                        // Unchecking View drops Edit with it — the API refuses
                                                        // edit-without-view, and silently sending an invalid pair
                                                        // would surface as a mystery 400.
                                                        onSet(role.id, page.id, nextView, nextView ? edit : false,
                                                            `${role.roleName} · ${page.pageName}`);
                                                    }}
                                                />
                                                View
                                            </label>
                                            <label className="flex items-center gap-1 text-xs text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={edit}
                                                    disabled={!canEdit || busy}
                                                    onChange={e => {
                                                        const nextEdit = e.target.checked;
                                                        onSet(role.id, page.id, nextEdit ? true : view, nextEdit,
                                                            `${role.roleName} · ${page.pageName}`);
                                                    }}
                                                />
                                                Edit
                                            </label>
                                        </div>
                                        {grant && (
                                            // P38, task 7 (D5). This was the app's only
                                            // `toLocaleDateString()` — a THIRD date format on a
                                            // page that already had two, and one whose output
                                            // depends on the reader's browser locale, so two
                                            // colleagues comparing the same matrix would see
                                            // `28.09.2026` and `9/28/2026`. Task 6 takes the
                                            // 10 px with it.
                                            <div className="mt-0.5 text-center text-xs text-muted">
                                                {isoDate(grant.updatedAt)} · {grant.updatedBy}
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------

export default function Settings() {
    const { user, refreshProfile } = useAuth();
    const { canEdit } = usePagePermissions('/settings');
    const { toast } = useToast();

    const users = useSettingsUsers();
    const roles = useSettingsRoles();
    const matrix = usePermissionMatrix();
    const updateUserRole = useUpdateUserRole();
    const updateMatrix = useUpdatePermissionMatrix();

    const [confirm, setConfirm] = useState<{ title: string; message: string; run: () => void } | null>(null);

    const busy = updateUserRole.isPending || updateMatrix.isPending;
    const roleList = useMemo(() => roles.data ?? [], [roles.data]);

    const applyRoleChange = async (email: string, roleId: number, action: 'assign' | 'remove', roleName: string) => {
        try {
            const result = await updateUserRole.mutateAsync({ email, roleId, action });
            if (!result.changed) {
                toast(`No change — ${result.reason ?? 'already in that state'}`, 'info');
            } else {
                toast(`${roleName} ${action === 'assign' ? 'assigned to' : 'removed from'} ${email}`, 'success');
            }
            // The signed-in user's own grants changed → their once-per-load
            // profile is now wrong. Everyone else picks it up on their next load.
            if (user && email.toLowerCase() === user.email.toLowerCase()) {
                await refreshProfile();
            }
        } catch (err) {
            toast(errorMessage(err), 'error');
        }
    };

    const onUserChange = (email: string, roleId: number, action: 'assign' | 'remove', roleName: string) => {
        if (action === 'remove') {
            setConfirm({
                title: `Remove ${roleName}?`,
                message: `${email} loses every page that role grants. The assignment is kept as a deactivated row, `
                    + 'so you can give it back at any time — and both audit ledgers record this.',
                run: () => void applyRoleChange(email, roleId, action, roleName),
            });
            return;
        }
        void applyRoleChange(email, roleId, action, roleName);
    };

    const onMatrixSet = async (roleId: number, pageId: number, canViewNext: boolean, canEditNext: boolean, label: string) => {
        try {
            const result = await updateMatrix.mutateAsync({ roleId, pageId, canView: canViewNext, canEdit: canEditNext });
            toast(result.changed ? `${label} updated` : `No change — ${result.reason ?? 'already set'}`,
                result.changed ? 'success' : 'info');
            await refreshProfile();
        } catch (err) {
            toast(errorMessage(err), 'error');
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-ink">Settings</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Roles, user-role assignments and the page permission matrix. Every change here is written to both
                    audit ledgers.
                </p>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                    A change takes effect on the server immediately. Other people&apos;s browsers read their permissions
                    once per page load — so for anybody but you, the change becomes visible when they next reload DRA.
                </span>
            </div>

            <Section title="System status" icon={ShieldCheck} subtitle="Live from GET /api/v1/agent/status — the two send locks and the current queues.">
                <SystemStatus />
            </Section>

            <Section
                title="People and roles"
                icon={Users}
                subtitle="Everyone who has ever been given a role. Removing a role deactivates the assignment; it is never deleted."
            >
                {users.isLoading || roles.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : users.error ? (
                    <div className="text-sm text-red-700">{errorMessage(users.error)}</div>
                ) : (
                    <>
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                    <th className="px-3 py-2 font-medium">Address</th>
                                    <th className="px-3 py-2 font-medium">Roles</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {(users.data ?? []).map(u => (
                                    <UserRow
                                        key={u.email}
                                        user={u}
                                        roles={roleList}
                                        canEdit={canEdit}
                                        busy={busy}
                                        onChange={onUserChange}
                                    />
                                ))}
                            </tbody>
                        </table>
                        {canEdit && <AddUserForm roles={roleList} busy={busy} onAdd={(email, roleId, roleName) => onUserChange(email, roleId, 'assign', roleName)} />}
                    </>
                )}
            </Section>

            <Section title="Roles" icon={Mail} subtitle="Fixed in code — Admin drives the permission override, Sales drives the authorization queue. A new role needs a migration.">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {roleList.map(r => (
                        <div key={r.id} className="rounded border border-gray-200 p-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-900">{r.roleName}</span>
                                <span className="text-xs text-gray-500">{r.activeUsers} {r.activeUsers === 1 ? 'person' : 'people'}</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{r.description ?? '—'}</p>
                            {/* P40, task 1: 11 px -> 12 px, and `amber-700` (4.13:1) onto
                                `--warn-ink` (7.71:1 on the warn tint). A sentence about a role that
                                bypasses the permission matrix is exactly what §3's floor is for.
                                The comment sits OUTSIDE the `&& (` - inside it is a syntax error
                                that the dev server reports as a blank page (P39 §4.5). */}
                            {r.roleName === 'Admin' && (
                                <p className="mt-2 flex items-center gap-1 text-xs text-warn-ink">
                                    <Check className="h-3 w-3" /> Bypasses the matrix — full access by code
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </Section>

            <Section
                title="Permission matrix"
                icon={Lock}
                subtitle="What each role may see and change. Admin is not listed: it bypasses this table entirely. /settings cannot be granted to any role."
            >
                {matrix.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                ) : matrix.error || !matrix.data ? (
                    <div className="text-sm text-red-700">{errorMessage(matrix.error)}</div>
                ) : (
                    <MatrixTable matrix={matrix.data} canEdit={canEdit} busy={busy} onSet={onMatrixSet} />
                )}
            </Section>

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    confirmLabel="Remove"
                    onConfirm={() => { confirm.run(); setConfirm(null); }}
                    onCancel={() => setConfirm(null)}
                />
            )}
        </div>
    );
}
